import type { Block } from '@/domain/model/Block'

const MAX_STRING_LENGTH = 80

// Truncates every string value found anywhere inside `value`, recursively,
// so a page with long text content stays cheap to send as LLM input. One
// generic helper reused for every block type instead of a bespoke
// summarizer per type — the AI assistant only needs enough of each field to
// recognize which block the user means, not the full content.
export function truncateStrings(value: unknown, maxLength = MAX_STRING_LENGTH): unknown {
  if (typeof value === 'string') {
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
  }
  if (Array.isArray(value)) {
    return value.map((v) => truncateStrings(v, maxLength))
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, truncateStrings(v, maxLength)]),
    )
  }
  return value
}

export interface CompactBlock {
  id: string
  type: string
  props: unknown
  settings: unknown
}

// Compact, token-cheap representation of the current page sent to the
// ai-assistant Edge Function — full block JSON with every string truncated,
// never the raw content. Settings are included (not just props) because
// style instructions like "make the hero darker" target settings fields
// (backgroundColor, textColor) as often as they target content.
export function summarizeBlocksForAi(blocks: Block[]): CompactBlock[] {
  return blocks.map((b) => ({
    id: b.id,
    type: b.type,
    props: truncateStrings(b.props),
    settings: truncateStrings(b.settings),
  }))
}
